import { z } from 'zod';

import { AppraisalId, brandedId, InspectionId } from '../BrandedIds';

/**
 * EntityKind names the loan-snapshot entity types that a workflow step can
 * pre-allocate an ID for. Used by `BaseStepDefinition.preallocatedEntityRefs`
 * and by predicates like `loanHasAppraisalWithId` / `loanHasInspectionWithId`.
 *
 * Add a new entry when introducing a new kind of entity that workflows need
 * to track by ID (e.g. workout plans, letters). Update `brandedIdForKind` in
 * lockstep.
 */
export const EntityKind = z.enum(['appraisal', 'inspection']);
export type EntityKind = z.infer<typeof EntityKind>;

export type EntityIdForKind<K extends EntityKind> = K extends 'appraisal'
  ? AppraisalId
  : K extends 'inspection'
    ? InspectionId
    : never;

/**
 * Produces a typed branded ID for the given entity kind. If `id` is omitted, a
 * random UUID is generated. Returned value is the appropriate `*Id` brand.
 */
export const brandedIdForKind = <K extends EntityKind>(kind: K, id?: string): EntityIdForKind<K> => {
  switch (kind) {
    case 'appraisal':
      return brandedId('AppraisalId', id) as EntityIdForKind<K>;
    case 'inspection':
      return brandedId('InspectionId', id) as EntityIdForKind<K>;
    default: {
      const exhaustive: never = kind;
      throw new Error(`Unhandled EntityKind: ${exhaustive as string}`);
    }
  }
};
